import _ from 'underscore';
import $ from 'jquery';
import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SchemaI18n } from '@bingads-webui-universal/schema-i18n';
import { useReportDataSource } from '@bingads-webui-campaign/reports-v4-data-source';
import { Callout, mergeStyleSets } from '@fluentui/react';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { FormGroup } from '@bingads-webui-campaign/inline-download-reports-grid';
import { InlineDownloadEmailFrequencyEditor } from '@bingads-webui-campaign/inline-download-email-frequency-editor';
import loadUsers from '@bingads-webui-campaign-legacy/inline-download-reports/load-users';
import { FrequencyEditPanel } from '@bingads-webui-campaign/reports-v4-schedule-frequency-icon-editor';
import { isReportTemplateType } from '../shared';

const styles = mergeStyleSets({
  callout: {
    padding: '24px 16px',
    background: '#fff',
    '& .ms-Callout-main': {
      overflow: 'visible',
      maxHeight: 'initial !important',
    },
  },
  spinner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: '3',
    background: 'rgba(255, 255, 255, .6)',
  },
  editor: {
    '& .inline-download-cron-editor': {
      display: 'flex',
      whiteSpace: 'pre',
      alignItems: 'center',

      '& .dropdown-menu': {
        overflow: 'auto',
        maxHeight: 360,
        display: 'none',
      },
      '& .open .dropdown-menu': {
        display: 'block',
      },
      '& > *': {
        marginRight: 4,
      },
    },
  },
  cancelBtn: {
    marginLeft: 8,
  },
});


function EmailReportEditor({
  item, legacyI18n, saveFrequency, onDismiss,
}) {
  const { scope } = useContext(ScopeContext);
  const { aid, uid } = scope.values;
  const allUsersType = aid ? 'Account' : 'Customer';
  const i18n = useContext(I18nContext);

  const property = ['NotificationDeliveryOption', 'Cron', 'StartTime', 'EndTime'];
  const initEmailModel = _.pick(item, ...property);
  const [emailModel, setEmailModel] = useState(initEmailModel);
  return (
    <div>
      <InlineDownloadEmailFrequencyEditor
        className={styles.editor}
        allUsersType={allUsersType}
        data={emailModel}
        currentUserId={uid}
        s$i18n={new SchemaI18n({ i18n: legacyI18n })}
        i18n={legacyI18n}
        onChange={(data) => { setEmailModel(data); }}
        FormGroup={FormGroup}
        loadUsers={() => loadUsers({ allUsersType })}
      />
      <div className={styles.actions}>
        <PrimaryButton onClick={() => saveFrequency(emailModel)} text={i18n.getString(_TL_('Save'))} />
        <DefaultButton className={styles.cancelBtn} onClick={onDismiss} text={i18n.getString(_TL_('Cancel'))} />
      </div>
    </div>
  );
}

EmailReportEditor.propTypes = {
  item: PropTypes.shape({}).isRequired,
  legacyI18n: PropTypes.shape({}).isRequired,
  onDismiss: PropTypes.func.isRequired,
  saveFrequency: PropTypes.func.isRequired,
};

function InlineFrequency(props) {
  const {
    target, item, onDismiss, onActionSuccess, notifyErrorPipe,
  } = props;
  const [loading, setLoading] = useState(false);
  const isReportTemplate = isReportTemplateType(item.Type);
  const i18n = useContext(I18nContext);
  const reportApi = useReportDataSource();

  async function saveFrequency(settings) {
    setLoading(true);
    try {
      const originTaskItem = await reportApi.getTaskItem(item.Id);
      await reportApi.patchTaskItem({ ...originTaskItem, ...settings }, { isEmailRule: !isReportTemplate });
      onDismiss();
      onActionSuccess();
    } catch (e) {
      notifyErrorPipe(e);
    } finally {
      setLoading(false);
    }
  }

  function handleDismiss(e) {
    const calendarContainer = $('.k-animation-container .k-calendar-container');
    if (calendarContainer.find(e.target).length > 0) {
      return;
    }
    props.onDismiss();
  }

  function renderReportTemplate() {
    const {
      Cron,
      AttachFileToEmail,
      EmailRecipients,
      StartTime,
      EndTime,
      Format,
    } = item;
    return (<FrequencyEditPanel
      onSave={saveFrequency}
      onCancel={onDismiss}
      data={{
        AttachFileToEmail,
        EmailRecipients,
        Cron,
        StartTime,
        EndTime,
        Template: item.Type,
        FileFormat: Format,
      }}
    />);
  }

  function renderEmail() {
    return (<EmailReportEditor
      item={item}
      legacyI18n={props.legacyI18n}
      saveFrequency={saveFrequency}
      onDismiss={handleDismiss}
    />);
  }

  return (
    <Callout
      coverTarget
      setInitialFocus
      doNotLayer
      preventDismissOnScroll
      directionalHint={0}
      directionalHintFixed
      isBeakVisible={false}
      target={target}
      className={`${styles.callout} inline-edit-popup`}
      onDismiss={handleDismiss}
    >
      {isReportTemplate ? renderReportTemplate() : renderEmail()}
      {loading && <Spinner className={styles.spinner} label={i18n.getString(_TL_('Loading...'))} labelPosition="right" />}
    </Callout>
  );
}

InlineFrequency.propTypes = {
  item: PropTypes.shape({}).isRequired,
  target: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  legacyI18n: PropTypes.shape({}).isRequired,
  onDismiss: PropTypes.func.isRequired,
  onActionSuccess: PropTypes.func.isRequired,
  notifyErrorPipe: PropTypes.func.isRequired,
};

function areEqual(prevProps, nextProps) {
  return nextProps.item !== prevProps.item;
}

export default React.memo(InlineFrequency, areEqual);
